import { useState, useEffect } from "react";
import fleetApi, { fleetError } from "@/lib/fleetApi";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Loader2, Sparkles, AlertTriangle } from "lucide-react";
import { fmtTime } from "@/lib/time";
import { driverOpenTrips, unfinishedBefore, clearDriverLoad } from "@/lib/driverLoad";
import { PriorityPanel } from "@/components/portal/AIPriorityPanel";
import { useAuth } from "@/context/AuthContext";

const darkField = "rounded-md border border-slate-700 bg-slate-900 px-2 py-1.5 text-xs text-white focus:outline-none focus:ring-2 focus:ring-blue-600";

// Used when the company has no saved priority presets yet.
const DEFAULT_PRESETS = [
  { id: "default_balanced", name: "Balanced", weights: { distance: 45, workload: 30, ontime: 25 } },
  { id: "default_short", name: "Short-staffed", weights: { distance: 25, workload: 55, ontime: 20 } },
  { id: "default_snow", name: "Snow day", weights: { distance: 25, workload: 20, ontime: 55 } },
];

// Trip codes (MART Veh Typ "W", WC, Wheelchair) that need a wheelchair-accessible vehicle.
export const needsWheelchair = (t) => !!t && (t.wheelchair === true || [t.trip_type, t.space_type, t.vehicle_type_code, ...(t.mobility_codes || [])]
  .some((c) => /^(w|wc|wch|wheel.*)$/i.test(String(c || "").trim())));

function PlanRow({ a, trip, drivers, checked, onToggle, onDriver, pending, wc, mismatch }) {
  return (
    <li className="flex flex-col gap-2 rounded-lg border border-slate-800 bg-slate-900/60 p-3 sm:flex-row sm:items-center" data-testid={`ai-plan-row-${a.trip_id}`}>
      <input type="checkbox" checked={checked && !mismatch} disabled={!!mismatch} onChange={onToggle} className="h-4 w-4 accent-blue-600" data-testid={`ai-plan-check-${a.trip_id}`} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-sm font-semibold text-white">
          {wc && <span className="mr-2 rounded bg-blue-500/15 px-1.5 py-0.5 text-[10px] font-bold uppercase text-blue-300" data-testid={`ai-plan-wc-${a.trip_id}`}>Wheelchair</span>}
          {trip?.rider_name || a.rider_name || a.external_trip_id || a.trip_id}
          <span className="ml-2 font-mono-data text-[11px] font-normal text-slate-500">{trip?.external_trip_id || a.external_trip_id}{trip?.scheduled_pickup ? ` · ${fmtTime(trip.scheduled_pickup)}` : ""}</span>
        </p>
        {(a.reason || a.explanation || a.why) && <p className="truncate text-xs text-slate-400">{a.reason || a.explanation || a.why}</p>}
        {mismatch && (
          <p className="mt-0.5 text-xs font-semibold text-red-300" data-testid={`ai-plan-mismatch-${a.trip_id}`}>
            Needs a wheelchair vehicle — {mismatch}. Pick a driver marked ♿.
          </p>
        )}
        {pending?.length > 0 && (
          <p className="mt-0.5 text-xs font-semibold text-amber-300" data-testid={`ai-plan-pending-${a.trip_id}`}
            title={pending.map((t) => `${t.external_trip_id} · ${t.status}`).join("\n")}>
            Driver still has {pending.length} unfinished earlier trip{pending.length === 1 ? "" : "s"} — untick to skip
          </p>
        )}
      </div>
      <select value={a.driver_id} onChange={(e) => onDriver(e.target.value)} className={darkField} data-testid={`ai-plan-driver-${a.trip_id}`}>
        {!drivers.some((d) => d.id === a.driver_id) && <option value={a.driver_id}>{a.driver_name || a.driver_id}</option>}
        {drivers.map((d) => <option key={d.id} value={d.id} disabled={wc && !d.wc}>{d.name}{d.wc ? " ♿" : ""}{wc && !d.wc ? " (no wheelchair vehicle)" : ""}</option>)}
      </select>
    </li>
  );
}

export default function AutoAssignDialog({ open, onOpenChange, tripIds, tripsById, scopeLabel, onManual, onConfirmed }) {
  const { user } = useAuth();
  const autoKey = `rs_ai_auto_accept_${user?.id || "me"}`;
  const [presets, setPresets] = useState([]);
  const [weights, setWeights] = useState(DEFAULT_PRESETS[0].weights);
  const [autoAccept, setAutoAcceptState] = useState(() => localStorage.getItem(autoKey) === "1");
  const setAutoAccept = (v) => { setAutoAcceptState(v); localStorage.setItem(autoKey, v ? "1" : "0"); };
  const [drivers, setDrivers] = useState([]);
  const [plan, setPlan] = useState(null);
  const [selected, setSelected] = useState({});
  const [busy, setBusy] = useState(false);
  const [openByDriver, setOpenByDriver] = useState({});

  useEffect(() => {
    const ids = [...new Set((plan?.assignments || []).map((a) => a.driver_id))].filter((id) => !(id in openByDriver));
    ids.forEach((id) => driverOpenTrips(id).then((open) => setOpenByDriver((m) => ({ ...m, [id]: open }))));
  }, [plan, openByDriver]);

  const loadPresets = (pick) => fleetApi.get("/assign-presets").catch(() => ({ data: [] })).then((r) => {
    const list = Array.isArray(r.data) && r.data.length ? r.data : DEFAULT_PRESETS;
    setPresets(list); if (pick) setWeights({ ...list[0].weights });
  });

  useEffect(() => {
    if (!open) return;
    setPlan(null); setSelected({}); setOpenByDriver({}); clearDriverLoad();
    loadPresets(true);
    Promise.all([fleetApi.get("/drivers"), fleetApi.get("/vehicles").catch(() => ({ data: [] }))]).then(([r, v]) => {
      const veh = Object.fromEntries((v.data || []).filter((x) => x.current_driver_id).map((x) => [x.current_driver_id, x]));
      setDrivers((r.data || []).map((d) => ({ id: d.id, name: d.full_name || d.name, veh: veh[d.id], wc: !!veh[d.id]?.wheelchair_accessible })));
    }).catch(() => setDrivers([]));
  }, [open]);

  const generate = async () => {
    setBusy(true);
    try {
      const r = await fleetApi.post("/trips/assign-plan", { trip_ids: tripIds, weights });
      setPlan(r.data);
      setSelected(Object.fromEntries((r.data.assignments || []).map((a) => [a.trip_id, true])));
      const all = r.data.assignments || [];
      const safe = all.filter((a) => (drivers.length ? !mismatchOf(a) : !needsWheelchair(tripOf(a))));
      if (autoAccept && safe.length) { setBusy(false); await confirm(safe, all.length - safe.length); return; }
    } catch (e) { toast.error(fleetError(e, "Couldn't build an AI plan.")); }
    finally { setBusy(false); }
  };

  const setDriver = (tripId, driverId) => setPlan((p) => ({ ...p, assignments: p.assignments.map((a) => (a.trip_id === tripId ? { ...a, driver_id: driverId, driver_name: drivers.find((d) => d.id === driverId)?.name } : a)) }));
  const tripOf = (a) => tripsById[a.trip_id] || a;
  const mismatchOf = (a) => {
    if (!needsWheelchair(tripOf(a))) return null;
    const d = drivers.find((x) => x.id === a.driver_id);
    if (d?.wc) return null;
    const who = d?.name || a.driver_name || "this driver";
    return d?.veh ? `${who} drives ${d.veh.fleet_number}, which isn't wheelchair accessible` : `${who} has no vehicle assigned`;
  };
  const mismatches = drivers.length ? (plan?.assignments || []).filter(mismatchOf) : [];
  const chosen = (plan?.assignments || []).filter((a) => selected[a.trip_id] && (!drivers.length || !mismatchOf(a)));

  const confirm = async (list = chosen, skipped = 0) => {
    setBusy(true);
    try {
      const r = await fleetApi.post("/trips/assign-confirm", { assignments: list });
      toast.success(`${r.data?.assigned ?? list.length} trip(s) assigned.${skipped ? ` ${skipped} wheelchair trip${skipped === 1 ? "" : "s"} skipped — needs a ♿ driver.` : ""}`);
      clearDriverLoad();
      onConfirmed?.();
      onOpenChange(false);
    } catch (e) { toast.error(fleetError(e, "Couldn't confirm the assignments.")); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto border-slate-800 bg-[#0f172a] text-white sm:max-w-2xl" data-testid="ai-assign-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Sparkles className="h-5 w-5 text-blue-400" /> AI auto-assign</DialogTitle>
          <DialogDescription className="text-slate-400">
            {tripIds ? `${tripIds.length} unassigned trip${tripIds.length === 1 ? "" : "s"} ${scopeLabel}` : "All upcoming unassigned trips"}. Nothing is assigned until you review and confirm.
          </DialogDescription>
        </DialogHeader>

        <PriorityPanel presets={presets} weights={weights} setWeights={setWeights} onPresetsChanged={() => loadPresets(false)}
          onPlan={generate} busy={busy} hasPlan={!!plan} disabled={tripIds && tripIds.length === 0}
          autoAccept={autoAccept} setAutoAccept={setAutoAccept} />

        {plan && (
          <div className="space-y-4" data-testid="ai-plan">
            <p className="text-sm text-slate-300" data-testid="ai-plan-summary">{plan.summary}</p>
            {mismatches.length > 0 && (
              <p className="flex items-start gap-2 rounded-md border border-red-500/30 bg-red-500/10 px-3 py-2 text-xs text-red-200" data-testid="ai-plan-wc-warning">
                <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                {mismatches.length} wheelchair trip{mismatches.length === 1 ? " was" : "s were"} matched to a driver without a wheelchair vehicle. They won't be assigned until you pick a ♿ driver.
              </p>
            )}
            {(plan.assignments || []).length > 0 && (
              <ul className="space-y-2">
                {plan.assignments.map((a) => (
                  <PlanRow key={a.trip_id} a={a} trip={tripsById[a.trip_id]} drivers={drivers} checked={!!selected[a.trip_id]}
                    onToggle={() => setSelected((s) => ({ ...s, [a.trip_id]: !s[a.trip_id] }))} onDriver={(id) => setDriver(a.trip_id, id)}
                    wc={needsWheelchair(tripOf(a))} mismatch={drivers.length ? mismatchOf(a) : null}
                    pending={unfinishedBefore(openByDriver[a.driver_id] || [], tripsById[a.trip_id] || { id: a.trip_id })} />
                ))}
              </ul>
            )}
            {(plan.unassignable || []).length > 0 && (
              <div className="rounded-lg border border-amber-500/30 bg-amber-500/5 p-3" data-testid="ai-unassignable">
                <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wider text-amber-300"><AlertTriangle className="h-3.5 w-3.5" /> AI couldn't match these</p>
                <ul className="space-y-1.5">
                  {plan.unassignable.map((u) => (
                    <li key={u.trip_id} className="flex items-center justify-between gap-2 text-xs text-slate-300" data-testid={`ai-unassignable-${u.trip_id}`}>
                      <span><span className="font-mono-data text-slate-400">{u.external_trip_id || u.trip_id}</span> — {u.reason}</span>
                      <button onClick={() => onManual(u.trip_id)} data-testid={`ai-manual-${u.trip_id}`}
                        className="shrink-0 rounded border border-slate-700 bg-slate-800/60 px-2 py-0.5 font-medium text-blue-300 hover:bg-slate-700">Assign manually</button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-300 hover:bg-slate-800 hover:text-white" data-testid="ai-cancel">Cancel</Button>
          <Button onClick={() => confirm()} disabled={busy || chosen.length === 0} className="gap-2 bg-emerald-600 text-white hover:bg-emerald-500" data-testid="ai-confirm-btn">
            {busy && plan ? <Loader2 className="h-4 w-4 animate-spin" /> : null} Confirm {chosen.length} assignment{chosen.length === 1 ? "" : "s"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
